import { Component, Input, ChangeDetectionStrategy } from "@angular/core"
import { COMMON_IMPORTS } from "../common-imports"
import { getChecked } from "@app/helpers"
import { ApiService } from "@app/services/api.service"
import { ModalService } from "@app/services/modal.service"
import { StoreService } from "@app/services/store.service"
import {
	BtnExtComponent,
	Check,
	SpinOrContent,
} from "@app/utils/utils.component"
import { GenInputComponent } from "@app/gen-inputs/gen-input/gen-input.component"
import { GenButtonGroupComponent } from "@app/gen-inputs/gen-button-group/gen-button-group.component"
import { KeyValuePipe } from "@angular/common"
import { LangValPipe } from "@app/utils/lang-val.pipe"
import { List } from "@app/policy-form-invite/list/list"

const findFn = (id: number, type: string) => (f) =>
	f.person_id == id && f.person_type == type

@Component({
	selector: "app-policy-form-invite",
	imports: [
		COMMON_IMPORTS,
		SpinOrContent,
		GenInputComponent,
		GenButtonGroupComponent,
		KeyValuePipe,
		Check,
		BtnExtComponent,
		LangValPipe,
		List,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: "./policy-form-invite.component.html",
})
export class PolicyFormInviteComponent {
	isLoading: boolean = false
	@Input() id: number = 0
	person_types: string[] = []
	personTypesObj: { [key: string]: any } = {}
	personTypesShown: { [key: string]: any } = {}
	isAllChecked: { [key: string]: any } = {}
	isFormSubmittable: boolean = false
	active: string = "yes"
	inviteFillOpt: string = "-1"
	isSending: boolean = false
	latestVersion: any = null
	serverObj: any = null
	isOpened: any = null
	search: string = ""

	constructor(
		public apiService: ApiService,
		public store: StoreService,
		public modalService: ModalService
	) {}

	async ngOnInit() {
		this.person_types = this.store.searchSiteDataItem(this.id, [
			"policy_forms",
		])?.person_types
		if (!this.person_types?.length) {
			return
		}
		this.isOpened = this.person_types.reduce(
			(cur, field) => ({ ...cur, [field]: true }),
			{}
		)
		this.loadPersonTypesObj()
	}
	async loadPersonTypesObj() {
		this.personTypesObj = {}
		this.isLoading = true
		const typeToIds: any = {}
		this.person_types.forEach((type) => {
			typeToIds[type] = this.store
				.getSiteDataTable(this.store.getPersonTypeTblName(type))
				.filter((it) => type !== "patient" || it?.is_group !== "yes")
				.map(({ id }) => id)
		})

		this.serverObj = await this.apiService.post("get_policy_form_filled_ids", {
			id: this.id,
			typeToIds,
		})
		console.log(this.serverObj)

		this.isLoading = false
		this.isAllChecked = {}

		this.personTypesObj = {}
		this.personTypesShown = {}
		const versions = this.store.getSortedVersions(this.id)
		console.log(versions)

		this.latestVersion = versions.at(0)

		this.person_types.forEach((type) => {
			this.personTypesObj[type] = this.store
				.getSiteDataTable(this.store.getPersonTypeTblName(type))
				.filter((it) => type !== "patient" || it?.is_group !== "yes")
				.map((it) => {
					const latestInvite = this.serverObj.invites.find(findFn(it.id, type))
					if (latestInvite) {
						latestInvite.version = versions.find(
							(it) => it.id == latestInvite.version_id
						)
					}

					const latestFill = this.serverObj.fills.find(findFn(it.id, type))
					if (latestFill) {
						latestFill.version = versions.find(
							(it) => it.id == latestFill.version_id
						)
					}
					const canSend = this.serverObj.typeToIdsCanSend[type].find(
						(id) => id == it.id
					)
					const canOpenFillModal =
						latestFill?.version?.id != this.latestVersion.id
					const hasCheckbox = canOpenFillModal && canSend

					return {
						...it,
						latestInvite,
						latest_invite_date: latestInvite?.created_at || 0,
						latestFill,
						latest_filled_date: latestFill?.created_at || 0,
						canSend,
						hasCheckbox,
						canOpenFillModal,
					}
				})
		})
		this.resetShown()
	}

	resetShown() {
		this.isAllChecked = {}
		this.person_types.forEach((type) => {
			this.personTypesObj[type].forEach((it) => (it.checked = false))
			this.personTypesShown[type] = this.personTypesObj[type]
				.filter((it) => this.active == "-1" || this.active == it.active)
				.filter((it) => {
					if (this.inviteFillOpt == "-1") {
						return true
					}
					const wasLatestInviteSent =
						it.latestInvite?.version?.id == this.latestVersion.id
					if (this.inviteFillOpt === "not_invited") {
						return !wasLatestInviteSent
					}
					if (this.inviteFillOpt === "not_filled") {
						return (
							wasLatestInviteSent &&
							it.latestFill?.version?.id != this.latestVersion.id
						)
					}
					return it.latestFill?.version?.id == this.latestVersion.id
				})
		})
		this.validateEntireForm()
	}

	check(row: any) {
		row.checked = !row.checked
		this.validateEntireForm()
	}
	rowHasCheckbox(row: any) {
		return row.latestFill?.version?.id != this.latestVersion.id && row.canSend
	}
	checkAll(type: string) {
		this.isAllChecked[type] = !this.isAllChecked[type]
		this.personTypesShown[type]
			.filter((it) => this.rowHasCheckbox(it))
			.forEach((it) => (it.checked = this.isAllChecked[type]))
		this.validateEntireForm()
	}
	validateEntireForm() {
		this.isFormSubmittable = !!this.getCheckedPersons().length
	}
	getCheckedPersons() {
		return this.person_types
			.map((type) =>
				getChecked(this.personTypesObj[type]).map((it) => ({ type, id: it.id }))
			)
			.flat()
	}
	async submit() {
		this.isSending = true
		const res = await this.apiService.post("invite_policy_form", {
			version_id: this.latestVersion.id,
			persons: this.getCheckedPersons(),
		})
		this.isSending = false
		this.modalService.openToast("sent_to")
	}
}
